"use client";

import { Accordion, Group, List, Paper, Text, ThemeIcon } from "@mantine/core";
import {
  IconFileText,
  IconHelp,
  IconMusic,
  IconVideo,
} from "@tabler/icons-react";
import { useTranslations } from "next-intl";

type PurchaseMode = "subscriptions" | "pay-as-you-go";

interface CreditCostAccordionProps {
  mode: PurchaseMode;
}

export function CreditCostAccordion({ mode }: CreditCostAccordionProps) {
  const t = useTranslations("components.CreditCostAccordion");
  const isSubscription = mode === "subscriptions";

  // Calculate totals based on mode
  const exampleMinutes = 4;
  const vocalIsolationCredits = exampleMinutes;
  const lyricsSyncCredits = exampleMinutes;
  const renderingCredits = isSubscription ? 0 : exampleMinutes;
  const totalCredits =
    vocalIsolationCredits + lyricsSyncCredits + renderingCredits;

  return (
    <Accordion variant="contained" radius="md">
      <Accordion.Item value="credit-cost">
        <Accordion.Control
          icon={
            <ThemeIcon size="sm" variant="light" color="gray" radius="xl">
              <IconHelp size={14} />
            </ThemeIcon>
          }
        >
          {t("title")}
        </Accordion.Control>
        <Accordion.Panel>
          <List size="sm" spacing="xs" mb="md">
            <List.Item
              icon={
                <ThemeIcon size="sm" variant="light" color="blue" radius="xl">
                  <IconMusic size={12} />
                </ThemeIcon>
              }
            >
              {t("vocalIsolation")}
            </List.Item>
            <List.Item
              icon={
                <ThemeIcon size="sm" variant="light" color="violet" radius="xl">
                  <IconFileText size={12} />
                </ThemeIcon>
              }
            >
              {t("lyricsSync")}
            </List.Item>
            <List.Item
              icon={
                <ThemeIcon
                  size="sm"
                  variant="light"
                  color={isSubscription ? "gray" : "orange"}
                  radius="xl"
                >
                  <IconVideo size={12} />
                </ThemeIcon>
              }
            >
              {t("videoExport")}{" "}
              {isSubscription ? (
                <>
                  <Text size="sm" c="dimmed" td="line-through" span>
                    {t("creditPerMinute")}
                  </Text>
                  <Text size="sm" c="green" span ml="xs">
                    {t("freeForSubscribers")}
                  </Text>
                </>
              ) : (
                t("creditPerMinute")
              )}
            </List.Item>
          </List>

          <Paper p="sm" radius="md" bg="dark.6">
            <Text size="sm" fw={600} mb="xs">
              {t("exampleTitle", { minutes: exampleMinutes })}
            </Text>
            <Group justify="space-between" mb={4}>
              <Text size="sm" c="dimmed">
                {t("breakdown.vocalIsolation")}
              </Text>
              <Text size="sm">
                {t("credits", { count: vocalIsolationCredits })}
              </Text>
            </Group>
            <Group justify="space-between" mb={4}>
              <Text size="sm" c="dimmed">
                {t("breakdown.lyricsSync")}
              </Text>
              <Text size="sm">
                {t("credits", { count: lyricsSyncCredits })}
              </Text>
            </Group>
            <Group justify="space-between" mb={4}>
              <Text size="sm" c="dimmed">
                {t("breakdown.videoExport")}
              </Text>
              {isSubscription ? (
                <Text size="sm">
                  <Text span td="line-through" c="dimmed">
                    {t("credits", { count: exampleMinutes })}
                  </Text>
                  <Text span c="green" ml="xs">
                    {t("free")}
                  </Text>
                </Text>
              ) : (
                <Text size="sm">
                  {t("credits", { count: renderingCredits })}
                </Text>
              )}
            </Group>
            <Group
              justify="space-between"
              pt="xs"
              style={{ borderTop: "1px solid var(--mantine-color-dark-4)" }}
            >
              <Text size="sm" fw={600}>
                {t("total")}
              </Text>
              <Text size="sm" fw={600} c="green">
                {t("credits", { count: totalCredits })}
              </Text>
            </Group>
          </Paper>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}
